import { Controller } from "@hotwired/stimulus"

/**
 * Tabs Controller - Tab-based navigation with URL hash synchronization.
 *
 * Usage:
 *   <div data-controller="tabs" data-tabs-active-class="tab-active">
 *     <nav role="tablist">
 *       <button data-tabs-target="tab" data-action="click->tabs#switch keydown->tabs#keydown" data-tab-panel="overview" role="tab">Overview</button>
 *       <button data-tabs-target="tab" data-action="click->tabs#switch keydown->tabs#keydown" data-tab-panel="guests" role="tab">Guests</button>
 *     </nav>
 *     <div data-tabs-target="panel" data-tab-name="overview" role="tabpanel">Overview content</div>
 *     <div data-tabs-target="panel" data-tab-name="guests" role="tabpanel">Guests content</div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["tab", "panel"]
  static values = {
    default: { type: String, default: "" }
  }
  static classes = ["active"]

  connect() {
    this.setupAriaIds()
    this.initializeFromHash()
    this.boundHandleHashChange = this.handleHashChange.bind(this)
    window.addEventListener("hashchange", this.boundHandleHashChange)
  }

  disconnect() {
    window.removeEventListener("hashchange", this.boundHandleHashChange)
  }

  setupAriaIds() {
    // Link tabs to their panels with aria-controls
    this.tabTargets.forEach((tab, index) => {
      const panelName = tab.dataset.tabPanel
      const panel = this.panelTargets.find(p => p.dataset.tabName === panelName)

      if (panel) {
        // Ensure panel has an ID
        if (!panel.id) {
          panel.id = `tabpanel-${panelName}-${index}`
        }

        // Ensure tab has an ID for aria-labelledby
        if (!tab.id) {
          tab.id = `tab-${panelName}-${index}`
        }

        tab.setAttribute("aria-controls", panel.id)
        panel.setAttribute("aria-labelledby", tab.id)
      }
    })
  }

  initializeFromHash() {
    const hash = window.location.hash.slice(1)
    const validTab = this.findTabByName(hash)
    
    if (validTab) {
      this.activateTab(hash)
    } else {
      const defaultTab = this.defaultValue || this.tabTargets[0]?.dataset.tabPanel
      if (defaultTab) {
        this.activateTab(defaultTab)
      }
    }
  }

  handleHashChange() {
    const hash = window.location.hash.slice(1)
    if (this.findTabByName(hash)) {
      this.activateTab(hash)
    }
  }

  switch(event) {
    event.preventDefault()
    const tabName = event.currentTarget.dataset.tabPanel
    if (tabName) {
      this.activateTab(tabName)
      this.updateHash(tabName)
    }
  }

  activateTab(tabName) {
    this.tabTargets.forEach(tab => {
      const isActive = tab.dataset.tabPanel === tabName
      
      if (this.hasActiveClass) {
        tab.classList.toggle(this.activeClass, isActive)
      }
      
      tab.setAttribute("aria-selected", isActive.toString())
      tab.setAttribute("tabindex", isActive ? "0" : "-1")
    })

    this.panelTargets.forEach(panel => {
      const isActive = panel.dataset.tabName === tabName
      panel.hidden = !isActive
      panel.setAttribute("aria-hidden", (!isActive).toString())
    })

    this.element.dispatchEvent(new CustomEvent("tabs:changed", {
      detail: { tab: tabName },
      bubbles: true
    }))
  }

  updateHash(tabName) {
    const url = new URL(window.location)
    url.hash = tabName
    window.history.replaceState({}, "", url)
  }

  findTabByName(name) {
    return this.tabTargets.find(tab => tab.dataset.tabPanel === name)
  }

  keydown(event) {
    const tabs = this.tabTargets
    const currentIndex = tabs.indexOf(event.currentTarget)
    
    let nextIndex
    switch (event.key) {
      case "ArrowLeft":
        nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1
        break
      case "ArrowRight":
        nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0
        break
      case "Home":
        nextIndex = 0
        break
      case "End":
        nextIndex = tabs.length - 1
        break
      default:
        return
    }

    event.preventDefault()
    const nextTab = tabs[nextIndex]
    nextTab.focus()
    nextTab.click()
  }
}
